<template>
   <!-- ToDo's: Critical / Important / Completed (sample tasks for now) -->
   <div v-if="!closed" class="card bg-sa-card todos mb-4" :class="{ 'td-fullscreen': fullscreen }">
      <!-- Header -->
      <div class="card-header bg-sa-card-header td-header">
         <span class="td-title"><i class="fa fa-check me-2"></i>ToDo's</span>
         <div class="td-controls">
            <button type="button" class="td-ctrl" :title="minimized ? 'Show' : 'Minimize'" @click="minimized = !minimized">
               <i class="fa" :class="minimized ? 'fa-plus' : 'fa-minus'"></i>
            </button>
            <button type="button" class="td-ctrl d-none d-md-inline-flex" title="Full screen" @click="fullscreen = !fullscreen">
               <i class="fa" :class="fullscreen ? 'fa-compress' : 'fa-expand'"></i>
            </button>
            <button type="button" class="td-ctrl" title="Close" @click="closed = true">
               <i class="fa fa-times"></i>
            </button>
         </div>
      </div>

      <div v-show="!minimized" class="card-body bg-sa-card-body p-0">
         <template v-for="group in groups" :key="group.key">
            <!-- Group title (drop a task here to move it into this group) -->
            <h5 class="td-group" @dragover.prevent @drop="dropOnGroup(group.key)">
               <i :class="group.icon" class="me-1"></i>{{ group.label }}
               <small>({{ tasksIn(group.key).length }})</small>
            </h5>

            <div
               v-for="task in tasksIn(group.key)"
               :key="task.id"
               class="td-item"
               :class="{ 'td-done': task.done, 'td-dragging': dragId === task.id }"
               :draggable="!task.done"
               @dragstart="dragId = task.id"
               @dragend="dragId = null"
               @dragover.prevent
               @drop.stop="dropOnTask(task)"
            >
               <span class="td-handle" :class="{ invisible: task.done }" title="Drag to move"></span>
               <label class="td-check">
                  <input type="checkbox" :checked="task.done" :title="task.done ? 'Mark as not done' : 'Mark as done'" @change="toggleDone(task)" />
               </label>
               <div class="td-text">
                  <p class="td-line">
                     <span class="td-ticket">Ticket #{{ task.ticket }}</span> - {{ task.title }}
                     <a v-if="task.issue" href="#" @click.prevent>#{{ task.issue }}</a>
                     <span v-if="task.note">({{ task.note }})</span>
                     <template v-if="task.details">
                        [<a href="#" @click.prevent="$emit('more-details', task)">More Details</a>]
                     </template>
                  </p>
                  <small v-if="task.description" class="td-desc">{{ task.description }}</small>
                  <small class="td-date">{{ task.date }}</small>
               </div>
            </div>

            <!-- Empty group -->
            <div v-if="tasksIn(group.key).length === 0" class="td-empty" @dragover.prevent @drop="dropOnGroup(group.key)">
               {{ group.key === 'completed' ? 'Nothing completed yet.' : 'No tasks here.' }}
            </div>
         </template>
      </div>
   </div>
</template>

<script>
export default {
   name: "Todos",
   emits: ["more-details"],
   data() {
      return {
         closed: false,
         minimized: false,
         fullscreen: false,
         dragId: null,
         groups: [
            { key: "critical", label: "Critical Tasks", icon: "fa fa-warning" },
            { key: "important", label: "Important Tasks", icon: "fa fa-exclamation" },
            { key: "completed", label: "Completed Tasks", icon: "fa fa-check" },
         ],
         // Sample tasks. priority = the group it belongs to when not done
         tasks: [
            { id: 1, ticket: "17643", title: "Hotfix for WebApp interface issue", details: true, description: "Sea deep blessed bearing under darkness from God air living isn't.", date: "Jan 1, 2026", priority: "critical", done: false },
            { id: 2, ticket: "1347", title: "Inbox email is being sent twice", note: "bug fix", details: true, date: "Nov 22, 2025", priority: "important", done: false },
            { id: 3, ticket: "1314", title: "Call customer support re: Issue", issue: "6134", note: "code review", date: "Nov 22, 2025", priority: "important", done: false },
            { id: 4, ticket: "17643", title: "Hotfix for WebApp interface issue", details: true, description: "Sea deep blessed bearing under darkness from God air living isn't.", date: "Jan 1, 2026", priority: "important", done: false },
            { id: 5, ticket: "17643", title: "Hotfix for WebApp interface issue", details: true, description: "Sea deep blessed bearing under darkness from God air living isn't.", date: "Jan 1, 2026", priority: "critical", done: true },
         ],
      };
   },
   methods: {
      // Tasks shown under a group (Completed = every ticked task)
      tasksIn(groupKey) {
         if (groupKey === "completed") return this.tasks.filter((t) => t.done);
         return this.tasks.filter((t) => !t.done && t.priority === groupKey);
      },
      // Tick = move to Completed; untick = back to its own group
      toggleDone(task) {
         task.done = !task.done;
      },
      // Drag a task onto another task: it moves just before it (and into that task's group)
      dropOnTask(target) {
         const moving = this.tasks.find((t) => t.id === this.dragId);
         this.dragId = null;
         if (!moving || moving.id === target.id || target.done) return;
         moving.priority = target.priority;
         this.tasks.splice(this.tasks.indexOf(moving), 1);
         this.tasks.splice(this.tasks.indexOf(target), 0, moving);
      },
      // Drag a task onto a group title (or an empty group): it moves to the end of that group
      dropOnGroup(groupKey) {
         const moving = this.tasks.find((t) => t.id === this.dragId);
         this.dragId = null;
         if (!moving || groupKey === "completed") return;
         moving.priority = groupKey;
         this.tasks.splice(this.tasks.indexOf(moving), 1);
         this.tasks.push(moving);
      },
   },
};
</script>

<style scoped>
/* ---------- Header ---------- */
.td-header {
   display: flex;
   align-items: stretch;
   justify-content: space-between;
   padding-top: 0;
   padding-bottom: 0;
}
.td-title {
   display: flex;
   align-items: center;
   padding: 0.6rem 0;
}
.td-controls {
   display: flex;
   margin-right: calc(-1 * var(--bs-card-cap-padding-x, 1rem));
}
.td-ctrl {
   display: inline-flex;
   align-items: center;
   padding: 0 0.85rem;
   background: transparent;
   border: 0;
   border-left: 1px solid var(--sa-border, rgba(128, 128, 128, 0.3));
   color: inherit;
}
.td-ctrl:hover {
   background: var(--table-row-bg-hover, rgba(128, 128, 128, 0.12));
}

/* ---------- Group titles ---------- */
.td-group {
   margin: 0;
   padding: 0.45rem 0.75rem;
   font-size: 1.05rem;
   font-weight: 400;
   opacity: 0.7;
   background: rgba(128, 128, 128, 0.08);
   border-top: 1px solid var(--sa-border, rgba(128, 128, 128, 0.25));
   border-bottom: 1px solid var(--sa-border, rgba(128, 128, 128, 0.25));
}
.td-group small {
   font-size: 0.85em;
}

/* ---------- Task rows (notepad look: pink margin line) ---------- */
.td-item {
   display: flex;
   align-items: flex-start;
   border-bottom: 1px solid var(--sa-border, rgba(128, 128, 128, 0.2));
}
.td-item.td-dragging {
   opacity: 0.4;
}
.td-handle {
   flex: 0 0 0.9rem;
   align-self: stretch;
   margin: 0.6rem 0 0.6rem 0.4rem;
   cursor: grab;
   /* little dots */
   background-image: radial-gradient(rgba(128, 128, 128, 0.6) 1px, transparent 1px);
   background-size: 4px 4px;
   max-height: 1.4rem;
}
.td-check {
   flex: 0 0 2.6rem;
   display: flex;
   justify-content: center;
   padding-top: 0.6rem;
   margin: 0;
}
.td-check input {
   width: 1.15rem;
   height: 1.15rem;
   cursor: pointer;
   accent-color: var(--sa-accent, #3276b1);
}
.td-text {
   flex: 1 1 auto;
   min-width: 0;
   padding: 0.55rem 0.75rem;
   border-left: 1px solid rgba(220, 53, 69, 0.35);   /* the pink margin line */
}
.td-line {
   margin: 0;
}
.td-ticket {
   font-weight: 700;
   opacity: 0.55;
}
.td-desc,
.td-date {
   display: block;
   font-size: 0.7rem;
   opacity: 0.55;
}
.td-text a {
   color: var(--bs-link-color);
   text-decoration: none;
}
/* Completed: struck through */
.td-done .td-text {
   text-decoration: line-through;
   opacity: 0.8;
}
.td-empty {
   padding: 0.6rem 0.75rem 0.6rem 4rem;
   font-size: 0.85rem;
   opacity: 0.55;
}

/* ---------- Full screen ---------- */
.td-fullscreen {
   position: fixed;
   inset: 0;
   z-index: 1050;
   margin: 0 !important;
   overflow: auto;
}
</style>